import { motion, AnimatePresence } from "framer-motion";
import { X, CaretLeft } from "@phosphor-icons/react";
import React, { useState } from "react";
import logo from "../assets/images/visaro-logo-circle.svg";
import LegalContent from "../pages/LegalContent";
import PrivacyContent from "../pages/PrivacyContent";
import TermsContent from "../pages/TermsContent";
import { useAuth } from "../context/AuthContext";
import {
  acknowledgeWelcomeConsent,
  isWelcomeConsentAcknowledged,
} from "../utils/welcomeConsentAck";

type ModalView = "intro" | "legal";
type LegalTab = "legal" | "privacy" | "terms";

/**
 * Welcome/KYC-consent modal on the signup NIN step. Shows once per account:
 * "I Understand" is remembered for the user, so it never reappears later in the
 * flow. The X only dismisses it for this visit.
 */
const GetStartedModal = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [view, setView] = useState<ModalView>("intro");
  const [tab, setTab] = useState<LegalTab>("legal");

  // Wait for the user so an already-acknowledged account never sees a flash.
  const isOpen = !dismissed && !!user && !isWelcomeConsentAcknowledged(user.id);

  const handleClose = () => setDismissed(true);

  const handleAcknowledge = () => {
    acknowledgeWelcomeConsent(user?.id);
    setDismissed(true);
  };

  const renderLegalContent = () => {
    switch (tab) {
      case "privacy":
        return <PrivacyContent />;
      case "terms":
        return <TermsContent />;
      default:
        return <LegalContent />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className={`bg-white rounded-2xl shadow-lg w-full p-6 relative transition-all duration-300 ${
              view === "legal" ? "max-w-2xl" : "max-w-md"
            }`}
          >
            {/* Header Buttons */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              {view === "legal" ? (
                <button
                  onClick={() => setView("intro")}
                  className="p-2 rounded-full hover:bg-gray-100 transition flex items-center gap-1 text-sm font-medium text-gray-600"
                >
                  <CaretLeft size={18} weight="bold" />
                  Back
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-100 transition"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {view === "intro" ? (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="mt-6"
                >
                  {/* Header */}
                  <div className="flex flex-col gap-2 text-center mt-2 items-center justify-center">
                    <img
                      src={logo}
                      alt="Visaro's Logo"
                      className="w-20 rounded-full drop-shadow-sm"
                    />
                    <h2 className="text-xl font-semibold text-deepBlue">
                      Welcome to Visaro
                    </h2>
                    <p className="text-gray-500">
                      Before you continue, please take a moment to review how we
                      use your information.
                    </p>
                  </div>

                  {/* Body */}
                  <div className="mt-6 space-y-3 text-gray-600 text-sm">
                    <p>
                      By creating an account, you consent to Visaro collecting
                      and verifying your <strong>KYC information</strong> —
                      including your BVN, NIN, and contact details — to keep
                      your wallet secure.
                    </p>
                    <p>
                      We use this information to provide you with{" "}
                      <strong>personalized credit options</strong>, enable{" "}
                      <strong>faster verifications</strong>, and improve your
                      overall financial experience.
                    </p>
                    <p>
                      Your data is protected and handled in compliance with all
                      relevant privacy and data-protection regulations.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcknowledge}
                      className="p-4 px-5 flex-1 border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm hover:bg-opacity-90 transition"
                    >
                      I Understand, Get Started
                    </button>
                    <button
                      onClick={() => setView("legal")}
                      className="p-4 px-5 border-visaro-gray-700 border rounded-lg font-bold bg-transparent text-deepBlue text-sm hover:bg-gray-50 transition text-center"
                    >
                      Learn More
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="legal"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="mt-8"
                >
                  <h2 className="text-xl font-semibold text-deepBlue text-center mb-6">
                    Legal & Policies
                  </h2>

                  {/* Tabs */}
                  <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6">
                    {(["legal", "privacy", "terms"] as LegalTab[]).map((t) => (
                      <button
                        key={t}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                          tab === t
                            ? "bg-white text-deepBlue shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => setTab(t)}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>

                  {/* Content Area */}
                  <div className="max-h-[50vh] overflow-y-auto px-2 custom-scrollbar text-sm">
                    {renderLegalContent()}
                  </div>

                  {/* Action */}
                  <div className="mt-8 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleAcknowledge}
                      className="w-full p-4 px-5 border rounded-lg font-bold bg-deepBlue text-white text-sm hover:bg-opacity-90 transition"
                    >
                      I Understand, Continue
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GetStartedModal;
